"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/*

A Resource is a wrapper for a value that either already
exists or is in a loading state (empty, loading, error).

You can chain Resources with `.map()`. `.map()` transforms
a completed value if available or returns a loading state.
The loading state will essentially short-circuit the chain
of `.map`s.

Basic examples of using Resource:

const emptyResource = new Resource(null);
emptyResource.value(); // 'EMPTY'
emptyResource.completedOrFalse(); // false

const completedResource = new Resource(1);
completedResource.value(); // 1
completedResource.completedOrFalse(); // 1

const emptyResourcePlusOne = emptyResource
  .map(resource => resource + 1); // short-circuits to 'EMPTY'

const completedResourcePlusOne = completedResource
  .map(resource => resource + 1); // 2

// We can even redefine what 'EMPTY' means by using `flatMap` and
// returning a new `Resource`
const emptyResourceIsError = emptyResource
  .flatMap(resource => new Resource(resource + 1, {
    emptyMeans: 'ERROR'
  })); // short-circuits to 'ERROR'

The biggest benefit to Resource is that you can chain and not worry
about the loading state until the end of the chain:

const numberResource = new Resource(1);
const longResourceTransform = numberResource
  .map(resource => resource + 1)
  .map(resource => resource + 2)
  .map(resource => resource + 3);

longResourceTransform.value(); // 7
Here's a short-circuiting chain:

const numberResource = new Resource(1);
const longResourceTransform = numberResource
  .map(resource => resource + 1)
  .map(resource => null)
  .map(resource => resource + 3);

longResourceTransform.value(); // 'EMPTY'

You can inspect what state a Resource is in to make decisions:

const resource = new Resource({ error: true });
resource.isComplete(); // false
resource.isError(); // true
resource.isEmpty(); // true
resource.isLoading(); // true

*/
var Resource = /*#__PURE__*/function () {
  // Creates a new Resource that inspects the passed value
  // and determines whether it is complete or if it is still
  // in a loading state. The `emptyMeans` parameter determines
  // what loading state is returned when a passed value is
  // empty (defaults to EMPTY, but in userland could mean that
  // the value is still loading or is an error).
  function Resource(resource) {
    var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$emptyMeans = _ref.emptyMeans,
      emptyMeans = _ref$emptyMeans === void 0 ? Resource.EMPTY : _ref$emptyMeans;
    _classCallCheck(this, Resource);
    // Allow creation without `new`
    if (!(this instanceof Resource)) {
      return new Resource(resource, {
        emptyMeans: emptyMeans
      });
    }
    if (!resource || Array.isArray(resource) && !resource.length) {
      this._value = emptyMeans;
    } else {
      this._value = resource;
    }
  }

  // Either transforms a completed resource or
  // returns a resource with a loading state.
  // You can chain `.map()` infinitely.
  return _createClass(Resource, [{
    key: "map",
    value: function map(func) {
      return this.flatMap(function (wrappedValue) {
        return new Resource(func(wrappedValue));
      });
    }

    // Like `.map()`, but requires the caller to return a
    // newly wrapped resource. This is useful when you
    // want to redefine what "empty" means (otherwise, you'd
    // be returning a Resource wrapping a Resource, hence `.flatMap()`).
  }, {
    key: "flatMap",
    value: function flatMap(func) {
      // Short-circuit if the value is a loading state
      if (!this.isComplete()) {
        return this;
      }
      var result = func(this._value);
      if (!(result instanceof Resource)) {
        throw new Error('You must wrap your return value in a Resource. Are you looking for "map"?');
      }
      return result;
    }

    // Merges two resources together. If both resources contain completed
    // values, `.concat()` returns a new Resource containing an array of
    // both Resources' values. If a single Resource contains a loading state,
    // return a Resource containing that loading state. If both Resources
    // contain loading states, return a Resource containing the most severe
    // state, in order of FAILED, EMPTY, and LOADING.

    // Useful for determining if two interdependent resources are ready.

    // Example:

    // const completedResource = new Resource('ayy');
    // const anotherCompletedResource = new Resource('lmao');

    // const mergedResource = completedResource.concat(anotherCompletedResource); // ['ayy', 'lmao']
    // const completedResource = new Resource('ayy');
    // const emptyResource = new Resource([]);

    // const mergedResource = completedResource.concat(emptyResource); // 'EMPTY'
    // const loadingResource = new Resource({ pending: true });
    // const failedResource = new Resource({ error: true });

    // const mergedResource = loadingResource.concat(failedResource); // 'FAILED'
  }, {
    key: "concat",
    value: function concat(other) {
      var statePrecedence = [Resource.LOADING, Resource.EMPTY, Resource.FAILED];
      var thisIndex = statePrecedence.indexOf(this._value);
      var otherIndex = statePrecedence.indexOf(other._value);
      if (thisIndex === -1 && otherIndex === -1) {
        return new Resource(Array.isArray(this._value) && this._value.every(function (item) {
          return Array.isArray(item);
        }) ? this._value.concat([other._value]) : [this._value, other._value]);
      }
      if (otherIndex > thisIndex) {
        return other;
      }
      if (otherIndex < thisIndex) {
        return this;
      }
      return other;
    }

    // Returns the raw value of the Resource, completed or not.
  }, {
    key: "value",
    value: function value() {
      return this._value;
    }

    // Determines if the resource contains a completed value.
  }, {
    key: "isComplete",
    value: function isComplete() {
      return this._value !== Resource.EMPTY && this._value !== Resource.LOADING && this._value !== Resource.FAILED;
    }

    // Determines if a resource is empty.
  }, {
    key: "isEmpty",
    value: function isEmpty() {
      return this._value === Resource.EMPTY;
    }
  }, {
    key: "isLoading",
    value: function isLoading() {
      return this._value === Resource.LOADING;
    }
  }, {
    key: "isError",
    value: function isError() {
      return this._value === Resource.FAILED;
    }
  }, {
    key: "completeOrFalse",
    value: function completeOrFalse() {
      return this.isComplete() && this._value;
    }

    // Debugging utility (useful for logging between map chains).

    // Example:

    // const longResourceTransform = numberResource
    //   .map(resource => resource + 1)
    //   .map(resource => resource + 2)
    //   .tap(resource => console.log(resource)) // 2
    //   .map(resource => resource + 3);
  }, {
    key: "tap",
    value: function tap(func) {
      func(this);
      return this;
    }
  }]);
}(); // Merges multiple resources together.
// const allResources = Resource.sum(
//   new Resource('ayy'),
//   new Resource('lmao'),
//   new Resource('ayy')
// ); // ['ayy', 'lmao', 'ayy']
Resource.sum = function () {
  for (var _len = arguments.length, resources = new Array(_len), _key = 0; _key < _len; _key++) {
    resources[_key] = arguments[_key];
  }
  return resources.reduce(function (prev, curr) {
    return prev.concat(curr);
  });
};
Resource.EMPTY = 'RESOURCE_EMPTY';
Resource.LOADING = 'RESOURCE_LOADING';
Resource.FAILED = 'RESOURCE_FAILED';

// Use this in the Resource constructor to signal
// that a resource is complete, but has no
// associated value.
Resource.COMPLETE = 'RESOURCE_COMPLETE';
var _default = exports["default"] = Resource;